// Forecast rows are built only from Soil Sight `generateSoilSightOutcomes` data.
import {
  BAR_GROUP_INNER_GAP_PX,
  BAR_GROUP_MIN_WIDTH_PX,
  BAR_WIDTH_BY_SCENARIO_COUNT,
  OUTCOME_VISIBLE_YEAR_CAP,
  SCENARIO_LINE_COLORS,
  SNAPSHOT_YEARS,
  YEAR_COLUMN_MIN_WIDTH_PX,
} from "consts";

import { ContributionMarginYearBreakdown } from "models";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { bandAvg, bandMax, bandMin } from "pages/app/scenario-analysis/utils/minAvgMax";

export type SoilSightOutcomesByScenario = Record<string, ContributionMarginYearBreakdown[]>;

/** "difference" rebases on the baseline and shows one comparison scenario; it is cumulative only. */
export type ViewMode = "cumulative" | "annual" | "yield" | "resilience" | "difference";

/** The chart colour of the scenario at `index`; the palette cycles once scenarios outnumber it. */
export const scenarioLineColorAt = (index: number): string => SCENARIO_LINE_COLORS[index % SCENARIO_LINE_COLORS.length].line;

/** The chart colour a scenario is drawn in — its position in the list, so every surface agrees. */
export const scenarioLineColor = (scenarios: Array<{ id: string }>, scenarioId: string | undefined): string =>
  scenarioLineColorAt(
    Math.max(
      0,
      scenarios.findIndex((scenario) => scenario.id === scenarioId)
    )
  );

/** The per-scenario values a forecast row holds; `low`/`high` bound the cumulative line. */
export type ForecastKeyKind =
  | "name"
  | "cropId"
  | "cumulative"
  | "low"
  | "high"
  | "annual"
  | "annual_low"
  | "annual_high"
  | "yield"
  | "yield_low"
  | "yield_high";

/** The forecast row key holding one scenario's value of the given kind. */
export const forecastKey = (scenarioIdx: number, kind: ForecastKeyKind): string => `scenario_${scenarioIdx}_${kind}`;

/** The band keys behind a view's tooltip range; null for views without a band. */
export const getBandKinds = (viewMode: ViewMode): { low: ForecastKeyKind; high: ForecastKeyKind } | null => {
  if (viewMode === "cumulative") return { low: "low", high: "high" };
  if (viewMode === "annual") return { low: "annual_low", high: "annual_high" };
  if (viewMode === "yield") return { low: "yield_low", high: "yield_high" };
  return null;
};

export interface ForecastDatum {
  year: string;
  yearNum: number;
  [key: string]: number | string;
}

export type OutcomeFreshness = {
  isDirty: boolean;
  isCalculating: boolean;
  isAwaitingInitialCalculation: boolean;
  /** Promoted on crop hydration, before the run lands, so it is not sufficient on its own. */
  hasCalculated: boolean;
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
};

/** Whether the figures on screen no longer match the inputs. Shared by the results card and the
 *  Detailed calculations tab so the two can never disagree about it. */
export const areOutcomesStale = ({ isDirty, isCalculating, isAwaitingInitialCalculation, hasCalculated, soilSightOutcomes }: OutcomeFreshness): boolean =>
  isDirty || isCalculating || isAwaitingInitialCalculation || !hasCalculated || Object.keys(soilSightOutcomes ?? {}).length === 0;

/**
 * Contribution margin timeline from Soil Sight only. Rows are `projectionYears`
 * (years 1..N; no year-0 anchor). Scenario columns use `forecastKey`.
 * Years missing from the API for a scenario contribute annual 0; scenarios with no
 * outcomes stay zero.
 */
export const buildForecastDataFromSoilSight = (
  scenarios: ScenarioData[],
  projectionYears: number,
  outcomesByScenarioId: SoilSightOutcomesByScenario
): ForecastDatum[] => {
  const byScenarioIdx: Array<Map<number, ContributionMarginYearBreakdown> | null> = scenarios.map((scenario) => {
    const apiYears = outcomesByScenarioId[scenario.id] ?? [];
    if (apiYears.length === 0) return null;
    return new Map(apiYears.map((y) => [y.year, y]));
  });

  const runningPerIdx = new Map<number, number>();
  const runningLowPerIdx = new Map<number, number>();
  const runningHighPerIdx = new Map<number, number>();
  scenarios.forEach((_, idx) => {
    runningPerIdx.set(idx, 0);
    runningLowPerIdx.set(idx, 0);
    runningHighPerIdx.set(idx, 0);
  });

  const data: ForecastDatum[] = [];

  for (let i = 1; i <= projectionYears; i += 1) {
    const yearData: ForecastDatum = {
      year: String(i),
      yearNum: i,
    };

    scenarios.forEach((scenario, idx) => {
      yearData[forecastKey(idx, "name")] = scenario.title;

      const byYear = byScenarioIdx[idx];
      const apiEntry = byYear?.get(i);
      const total = apiEntry?.contributionMargin.total;
      const yearlyAvg = bandAvg(total);
      const yearlyLow = bandMin(total);
      const yearlyHigh = bandMax(total);

      const running = (runningPerIdx.get(idx) ?? 0) + yearlyAvg;
      const runningLow = (runningLowPerIdx.get(idx) ?? 0) + yearlyLow;
      const runningHigh = (runningHighPerIdx.get(idx) ?? 0) + yearlyHigh;
      runningPerIdx.set(idx, running);
      runningLowPerIdx.set(idx, runningLow);
      runningHighPerIdx.set(idx, runningHigh);

      yearData[forecastKey(idx, "annual")] = Math.round(yearlyAvg);
      // Per-year band, not accumulated — the annual view shows one year at a time.
      yearData[forecastKey(idx, "annual_low")] = Math.round(yearlyLow);
      yearData[forecastKey(idx, "annual_high")] = Math.round(yearlyHigh);
      yearData[forecastKey(idx, "cumulative")] = Math.round(running);
      // Summing per-year bounds assumes the same soil run is the extreme every year — true while the CM
      // response is monotonic in SOC; otherwise this band is wider than any single run.
      yearData[forecastKey(idx, "low")] = Math.round(runningLow);
      yearData[forecastKey(idx, "high")] = Math.round(runningHigh);
      // Yield is a per-year stock (t/ha), so its band is the per-year min/max — not accumulated.
      const yieldBand = apiEntry?.adjustedCropMetrics?.cropYield;
      yearData[forecastKey(idx, "yield")] = bandAvg(yieldBand);
      yearData[forecastKey(idx, "yield_low")] = bandMin(yieldBand);
      yearData[forecastKey(idx, "yield_high")] = bandMax(yieldBand);
      yearData[forecastKey(idx, "cropId")] = apiEntry?.cropId ?? "";
    });

    data.push(yearData);
  }

  return data;
};

/** Bar width per scenario count (1–5), clamped to the Figma spec. */
export const getBarWidthByScenarioCount = (scenarioCount: number): number => {
  const clampedCount = Math.min(5, Math.max(1, scenarioCount));
  return BAR_WIDTH_BY_SCENARIO_COUNT[clampedCount] ?? 12;
};

/** Total px width for one year group (bars + gaps), no less than BAR_GROUP_MIN_WIDTH_PX. */
export const getBarGroupWidthPx = (scenarioCount: number): number => {
  const barWidth = getBarWidthByScenarioCount(scenarioCount);
  const innerWidth = scenarioCount * barWidth + Math.max(0, scenarioCount - 1) * BAR_GROUP_INNER_GAP_PX;
  return Math.max(BAR_GROUP_MIN_WIDTH_PX, innerWidth);
};

/** Year-group width (px) stretched so OUTCOME_VISIBLE_YEAR_CAP groups fill the available inner chart width, never below the Figma minimum. */
export const getStretchedBarGroupWidthPx = (scenarioCount: number, availableInnerWidthPx: number): number =>
  Math.max(getBarGroupWidthPx(scenarioCount), availableInnerWidthPx / OUTCOME_VISIBLE_YEAR_CAP);

/** The table's counterpart to `getStretchedBarGroupWidthPx`: year columns stretch so the same OUTCOME_VISIBLE_YEAR_CAP years fill the width the chart above uses. */
export const getStretchedYearColumnWidthPx = (availableInnerWidthPx: number): number =>
  Math.max(YEAR_COLUMN_MIN_WIDTH_PX, availableInnerWidthPx / OUTCOME_VISIBLE_YEAR_CAP);

/** Whether annual/yield views should enable horizontal scroll chrome. */
export const shouldUseOutcomeYearScroll = (projectionYears: number): boolean => projectionYears > OUTCOME_VISIBLE_YEAR_CAP;

/** All projection years 1..N for Annual and Yield table columns. */
export const getAllYears = (projectionYears: number): number[] => {
  const years: number[] = [];
  for (let i = 1; i <= projectionYears; i += 1) {
    years.push(i);
  }
  return years;
};

/** Returns the subset of SNAPSHOT_YEARS that fit inside `projectionYears`. */
export const getSnapshotYears = (projectionYears: number): number[] => SNAPSHOT_YEARS.filter((y) => y <= projectionYears);

export interface SnapshotCell {
  year: number;
  cm: number;
  low: number;
  high: number;
}

/**
 * Extract the snapshot cells for a single scenario from a forecast dataset.
 * Missing year rows (e.g. timeline < 15 and we ask for year 15) are filtered out.
 */
export const computeSnapshotColumns = (data: ForecastDatum[], scenarioIndex: number, projectionYears: number, mode: ViewMode = "annual"): SnapshotCell[] => {
  const years = getSnapshotYears(projectionYears);
  return years
    .map<SnapshotCell | null>((year) => {
      const row = data.find((d) => d.yearNum === year);
      if (!row) return null;
      const cmKey = forecastKey(scenarioIndex, mode === "annual" ? "annual" : "cumulative");
      return {
        year,
        cm: Number(row[cmKey] ?? 0),
        low: mode === "annual" ? Number.NaN : Number(row[forecastKey(scenarioIndex, "low")] ?? 0),
        high: mode === "annual" ? Number.NaN : Number(row[forecastKey(scenarioIndex, "high")] ?? 0),
      };
    })
    .filter((c): c is SnapshotCell => c !== null);
};

/** Lookup helper for the chart/table: returns the forecast row for a specific year number. */
export const getYearData = (data: ForecastDatum[], yearNum: number): ForecastDatum | null => data.find((d) => d.yearNum === yearNum) ?? null;
